import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import InputFilterItem from '../../delivery/components/InputFilterItem';
import DamagedLabelExampleModal from './DamagedLabelExampleModal';
import { HubSelectFilter } from '../../../components/filters/HubSelectFilter';
import { WarehouseSelectFilter } from '../../../components/filters/WarehouseSelectFilter';
import { MultiParcelStatusSelectFilter } from '../../../components/filters/MultiParcelStatusSelectFilter';
import { getSmartLabelClassName } from '../../../utils/labelWidthUtils';
import {
    DamagedLabelLookupInput,
    EMPTY_DAMAGED_LABEL_LOOKUP_INPUT,
} from '../types/damagedLabelLookupTypes';

const INPUT_HEIGHT_CLASS = 'h-8';
const INPUT_HEIGHT_STYLE: React.CSSProperties = {
    paddingTop: 0,
    paddingBottom: 0,
    lineHeight: '32px',
};

interface DamagedLabelLookupFilterProps {
    value: DamagedLabelLookupInput;
    onChange: React.Dispatch<React.SetStateAction<DamagedLabelLookupInput>>;
    onSearch: () => void;
    onReset: () => void;
    onPrintLabel: () => void;
    loading?: boolean;
    printLoading?: boolean;
}

const DamagedLabelLookupFilter: React.FC<DamagedLabelLookupFilterProps> = ({
    value,
    onChange,
    onSearch,
    onReset,
    onPrintLabel,
    loading = false,
    printLoading = false,
}) => {
    const { t } = useTranslation();
    const [exampleModalOpen, setExampleModalOpen] = useState(false);
    const [selectedHubId, setSelectedHubId] = useState<string | undefined>(undefined);

    const labelSortCode1 = String(t('central.parcels.filters.sortCode1'));
    const labelSortCode2 = String(t('central.parcels.filters.sortCode2'));
    const labelSortCode3 = String(t('central.parcels.filters.sortCode3'));
    const labelSenderName = String(t('damagedLabelLookup.filters.senderName'));
    const labelSenderAddress = String(t('damagedLabelLookup.filters.senderAddress'));
    const labelSenderPostalCode = String(t('damagedLabelLookup.filters.senderPostalCode'));
    const labelRecipientName = String(t('damagedLabelLookup.filters.recipientName'));
    const labelRecipientAddress = String(t('damagedLabelLookup.filters.recipientAddress'));
    const labelRecipientPostalCode = String(t('damagedLabelLookup.filters.recipientPostalCode'));
    const labelTracking = String(t('delivery.parcels.label.tracking'));
    const labelOrder = String(t('delivery.parcels.label.order'));
    const labelParcelStatus = String(t('central.parcels.filters.parcelStatus'));

    const col1Labels = useMemo(
        () => [labelSortCode1, labelSenderName, labelRecipientName, labelTracking],
        [labelSortCode1, labelSenderName, labelRecipientName, labelTracking]
    );
    const col2Labels = useMemo(
        () => [labelSortCode2, labelSenderAddress, labelRecipientAddress, labelOrder],
        [labelSortCode2, labelSenderAddress, labelRecipientAddress, labelOrder]
    );
    const col3Labels = useMemo(
        () => [labelSortCode3, labelSenderPostalCode, labelRecipientPostalCode, labelParcelStatus],
        [labelSortCode3, labelSenderPostalCode, labelRecipientPostalCode, labelParcelStatus]
    );

    const col1SpanClassName = useMemo(() => getSmartLabelClassName(col1Labels), [col1Labels]);
    const col2SpanClassName = useMemo(() => getSmartLabelClassName(col2Labels), [col2Labels]);
    const col3SpanClassName = useMemo(() => getSmartLabelClassName(col3Labels), [col3Labels]);

    useEffect(() => {
        if (!value.sortingCodeSegment1 && selectedHubId !== undefined) {
            setSelectedHubId(undefined);
        }
    }, [value.sortingCodeSegment1, selectedHubId]);

    const updateField = (field: keyof DamagedLabelLookupInput, fieldValue: string | undefined) => {
        onChange((prev) => ({
            ...prev,
            [field]: fieldValue || undefined,
        }));
    };

    // WarehouseSelectFilter 选择时会连续触发 onChange(warehouseId) 与 onSelectSecondSegment；
    // 必须用函数式更新，避免第二次写入覆盖第一次的 warehouseId（与 ParcelFilter 一致）
    const handleWarehouseIdChange = useCallback(
        (warehouseId: string | undefined) => {
            if (!warehouseId) {
                onChange((prev) => ({
                    ...prev,
                    warehouseId: undefined,
                    sortingCodeSegment2: undefined,
                }));
            } else {
                onChange((prev) => ({
                    ...prev,
                    warehouseId,
                }));
            }
        },
        [onChange]
    );

    const handleSecondSegmentChange = useCallback(
        (secondSegment: string | undefined) => {
            if (secondSegment) {
                onChange((prev) => ({
                    ...prev,
                    sortingCodeSegment2: secondSegment,
                }));
            }
        },
        [onChange]
    );

    const handleReset = () => {
        setSelectedHubId(undefined);
        onChange({ ...EMPTY_DAMAGED_LABEL_LOOKUP_INPUT });
        onReset();
    };

    const renderInputFilter = (
        label: string,
        spanClassName: string,
        fieldValue: string | undefined,
        field: keyof DamagedLabelLookupInput
    ) => (
        <InputFilterItem
            label={label}
            value={fieldValue}
            onChange={(e) => updateField(field, e.target.value)}
            spanClassName={spanClassName}
            inputClassName={INPUT_HEIGHT_CLASS}
            inputStyle={INPUT_HEIGHT_STYLE}
        />
    );

    return (
        <div className="mb-2 pl-0 py-2 [&_.ant-select]:h-8 [&_.ant-select-selector]:h-8 [&_.ant-input:not(textarea)]:h-8 [&_.ant-picker]:h-8">
            <Alert
                type="info"
                showIcon
                className="mb-4"
                message={
                    <span>
                        {t('damagedLabelLookup.hint')}{' '}
                        <Typography.Link onClick={() => setExampleModalOpen(true)}>
                            {t('damagedLabelLookup.viewExample')}
                        </Typography.Link>
                    </span>
                }
            />
            <DamagedLabelExampleModal
                open={exampleModalOpen}
                onClose={() => setExampleModalOpen(false)}
            />

            <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <HubSelectFilter
                        label={labelSortCode1}
                        value={value.sortingCodeSegment1}
                        onChange={(segment1, hubId) => {
                            if (!segment1) {
                                setSelectedHubId(undefined);
                                onChange((prev) => ({
                                    ...prev,
                                    sortingCodeSegment1: undefined,
                                    sortingCodeSegment2: undefined,
                                    warehouseId: undefined,
                                }));
                            } else {
                                setSelectedHubId(hubId);
                                onChange((prev) => ({
                                    ...prev,
                                    sortingCodeSegment1: segment1,
                                    sortingCodeSegment2: undefined,
                                    warehouseId: undefined,
                                }));
                            }
                        }}
                        spanClassName={col1SpanClassName}
                        placeholder={t('central.parcels.placeholders.selectHub') || 'Select HUB'}
                    />
                </div>
                <div className="min-w-0">
                    <WarehouseSelectFilter
                        label={labelSortCode2}
                        value={value.warehouseId}
                        onChange={handleWarehouseIdChange}
                        onSelectSecondSegment={handleSecondSegmentChange}
                        hubId={selectedHubId}
                        spanClassName={col2SpanClassName}
                        placeholder={
                            t('central.parcels.placeholders.selectWarehouse') || 'Select Warehouse'
                        }
                    />
                </div>
                <div className="min-w-0">
                    {renderInputFilter(
                        labelSortCode3,
                        col3SpanClassName,
                        value.sortingCodeSegment3,
                        'sortingCodeSegment3'
                    )}
                </div>
                <div className="min-w-0">
                    {renderInputFilter(
                        labelSenderName,
                        col1SpanClassName,
                        value.senderName,
                        'senderName'
                    )}
                </div>
                <div className="min-w-0">
                    {renderInputFilter(
                        labelSenderAddress,
                        col2SpanClassName,
                        value.senderAddress,
                        'senderAddress'
                    )}
                </div>
                <div className="min-w-0">
                    {renderInputFilter(
                        labelSenderPostalCode,
                        col3SpanClassName,
                        value.senderPostalCode,
                        'senderPostalCode'
                    )}
                </div>
                <div className="min-w-0">
                    {renderInputFilter(
                        labelRecipientName,
                        col1SpanClassName,
                        value.recipientName,
                        'recipientName'
                    )}
                </div>
                <div className="min-w-0">
                    {renderInputFilter(
                        labelRecipientAddress,
                        col2SpanClassName,
                        value.recipientAddress,
                        'recipientAddress'
                    )}
                </div>
                <div className="min-w-0">
                    {renderInputFilter(
                        labelRecipientPostalCode,
                        col3SpanClassName,
                        value.recipientPostalCode,
                        'recipientPostalCode'
                    )}
                </div>
                <div className="min-w-0">
                    {renderInputFilter(
                        labelTracking,
                        col1SpanClassName,
                        value.trackingNumber,
                        'trackingNumber'
                    )}
                </div>
                <div className="min-w-0">
                    {renderInputFilter(
                        labelOrder,
                        col2SpanClassName,
                        value.channelOrderNumber,
                        'channelOrderNumber'
                    )}
                </div>
                <div className="min-w-0">
                    <MultiParcelStatusSelectFilter
                        label={labelParcelStatus}
                        value={value.overallStatuses}
                        onChange={(statuses) =>
                            onChange((prev) => ({ ...prev, overallStatuses: statuses }))
                        }
                        spanClassName={col3SpanClassName}
                        placeholder={t('components.input.all') as string}
                    />
                </div>
            </div>

            <div className="flex flex-wrap gap-x-2 gap-y-4">
                <div className="flex flex-wrap gap-2">
                    <Button
                        type="primary"
                        loading={printLoading}
                        onClick={onPrintLabel}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('central.parcels.buttons.printLabel')}</span>
                    </Button>
                </div>
                <div className="flex flex-wrap gap-2 ml-auto">
                    <Button
                        type="primary"
                        loading={loading}
                        onClick={onSearch}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('damagedLabelLookup.actions.search')}</span>
                    </Button>
                    <Button onClick={handleReset} disabled={loading} className="w-auto min-w-24">
                        <span className="font-bold">{t('damagedLabelLookup.actions.reset')}</span>
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default DamagedLabelLookupFilter;
